import {inject, Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, EMPTY, filter, Observable, tap} from "rxjs";
import {
  BokkzSalarisClient,
  LoonJournaal, LoonStaat,
  LoonstaatEntity,
  RequestDto
} from './api.bokkz.salaris';
import {ErrorMessage} from './rekenclient.api';
import {WerkgeverEntity, WerknemerEntity} from "./api.bokkz.salaris";

@Injectable({providedIn: 'root'})
export class BokkzWageDataService implements OnDestroy {

  private bokkzClient = inject(BokkzSalarisClient);

  public werkgeverEntity = new BehaviorSubject<WerkgeverEntity>(this.getFromSessionStorage<WerkgeverEntity>('werkgeverEntity') || {} as WerkgeverEntity);
  public werknemerEntity = new BehaviorSubject<WerknemerEntity>(this.getFromSessionStorage<WerknemerEntity>('werknemerEntity') || {} as WerknemerEntity);
  public werknemerList = new BehaviorSubject<WerknemerEntity[]>(this.getFromSessionStorage<WerknemerEntity[]>('werknemerList') || {} as WerknemerEntity[]);
  public loonstaatEntity = new BehaviorSubject<LoonstaatEntity>(this.getFromSessionStorage<LoonstaatEntity>('loonstaatEntity') || {} as LoonstaatEntity);
  public loonJournaal = new BehaviorSubject<LoonJournaal>(this.getFromSessionStorage<LoonJournaal>('loonJournaal') || {} as LoonJournaal);
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public selectedPeriode = new BehaviorSubject<number>(1);
  public selectedTab = new BehaviorSubject<number>(0);
  public year = new BehaviorSubject<number>(2026);

  public werkgeverEntity$: Observable<WerkgeverEntity> = this.werkgeverEntity.asObservable();
  public werknemerEntity$: Observable<WerknemerEntity> = this.werknemerEntity.asObservable();
  public werknemerList$: Observable<WerknemerEntity> = this.werknemerList.asObservable();
  public loonstaatEntity$: Observable<LoonstaatEntity> = this.loonstaatEntity.asObservable();
  public loonJournaal$: Observable<LoonJournaal> = this.loonJournaal.asObservable();
  public selectedPeriode$: Observable<number> = this.selectedPeriode.asObservable();
  public selectedTab$: Observable<number> = this.selectedTab.asObservable();
  public selectedYear$: Observable<number> = this.year.asObservable();

  public patchWerknemerEntity(updater: (entity: WerknemerEntity) => WerknemerEntity): void {
    const current = this.werknemerEntity.value;
    this.werknemerEntity.next(updater(current));
  }

  calculateLatestWerknemerEntity(): Observable<LoonstaatEntity> {
    const werknemer = this.werknemerEntity.value;
    const requestedWerknemerId = werknemer?.id;

    if (!werknemer || !werknemer.werknemerGegevens || !werknemer.werknemerGegevens.dienstverbanden) {
      return EMPTY;
    }

    const dto = {
      dienstverband: werknemer.werknemerGegevens?.dienstverbanden[0],
      loonStaat: this.loonstaatEntity.value.loonStaat,
      year: this.loonstaatEntity.value.year,
    } as RequestDto

    return this.bokkzClient.getCalculatedLoonstaat(dto).pipe(
      filter((loonStaat): loonStaat is LoonStaat => loonStaat != null),
      tap((loonStaat) => {
        const activeWerknemerId = this.werknemerEntity.value?.id;

        if (activeWerknemerId && requestedWerknemerId && activeWerknemerId !== requestedWerknemerId) {
          return;
        }

        this.loonstaatEntity.next({
          ...(this.loonstaatEntity.value ?? {}),
          loonStaat,
        });
      })
    );
  }

  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  updateWerkgever(wg: WerkgeverEntity) {
    this.werkgeverEntity.next(wg)
  }

  updateWerknemer(wn: WerknemerEntity) {
    if (wn.werknemerGegevens?.dienstverbanden?.at(0)) {
      if (wn.werknemerGegevens.dienstverbanden[0].loonstaat != undefined) {
        this.updateLoonstaat(wn.werknemerGegevens.dienstverbanden[0].loonstaat)
      } else {
        this.updateLoonstaat({} as LoonstaatEntity)
      }
    } else {
      this.updateLoonstaat({} as LoonstaatEntity)
    }
    this.werknemerEntity.next(wn);
  }

  updateWerknemerList(list: WerknemerEntity[]) {
    this.werknemerList.next(list)
  }

  updateLoonstaat(loonstaat: LoonstaatEntity) {
    this.loonstaatEntity.next(loonstaat)
  }

  updateLoonJournaal(loonJournaal: LoonJournaal) {
    this.loonJournaal.next(loonJournaal)
  }

  updateSelectedTab(index: number) {
    this.selectedTab.next(index)
  }

  updateSelectedPeriode(index: number) {
    this.selectedPeriode.next(index)
  }

  ngOnDestroy(): void {
    this.werkgeverEntity.complete();
    this.werknemerEntity.complete();
    this.loonstaatEntity.complete();
    this.selectedPeriode.complete();
    this.selectedTab.complete();
    if (this.werkgeverEntity) {
      this.werkgeverEntity?.unsubscribe()
    }
    if (this.werknemerEntity) {
      this.werknemerEntity?.unsubscribe()
    }
    if (this.werknemerList) {
      this.werknemerList?.unsubscribe()
    }
    if (this.loonstaatEntity) {
      this.loonstaatEntity?.unsubscribe()
    }
    if (this.loonJournaal) {
      this.loonJournaal?.unsubscribe()
    }
    if (this.selectedTab) {
      this.selectedTab?.unsubscribe()
    }
    if (this.selectedPeriode) {
      this.selectedPeriode?.unsubscribe()
    }
  }
}
